import React, { ReactNode } from 'react';
import { ConfigurationProvider } from './ConfigurationContext';
import { SystemStatusProvider } from './SystemStatusContext';
import { AnomaliesProvider } from './AnomaliesContext';
import { DeviceProvider } from './DeviceContext';
import { TelemetryProvider } from './TelemetryContext';
import { TankDataProvider } from './TankDataContext';

interface DomainProvidersProps {
  children: ReactNode;
}

/**
 * Single wrapper composing all 6 domain context providers.
 *
 * Nesting order (outermost to innermost):
 * 1. ConfigurationProvider  -- fully independent (assets/farm locations)
 * 2. SystemStatusProvider   -- fully independent (system health)
 * 3. AnomaliesProvider      -- fully independent (anomaly list)
 * 4. DeviceProvider         -- independent fetch; other contexts may read devices
 * 5. TelemetryProvider      -- fetches independently; may correlate with devices
 * 6. TankDataProvider       -- fetches independently; may correlate with devices
 *
 * This order gives re-render isolation: an update to TelemetryProvider only
 * re-renders TelemetryProvider consumers, not DeviceProvider consumers.
 * (Pitfall 7 avoidance -- providers at the same depth level don't cascade.)
 */
export function DomainProviders({ children }: DomainProvidersProps) {
  return (
    <ConfigurationProvider>
      <SystemStatusProvider>
        <AnomaliesProvider>
          <DeviceProvider>
            <TelemetryProvider>
              <TankDataProvider>
                {children}
              </TankDataProvider>
            </TelemetryProvider>
          </DeviceProvider>
        </AnomaliesProvider>
      </SystemStatusProvider>
    </ConfigurationProvider>
  );
}

export default DomainProviders;
